import { z } from 'zod';
// Stapelnaht sammel107: die Primitiven kommen aus datenregeln.ts (deckungsbeweist
// gegen Migration 0112) -- drei parallel gebaute Vertragsdateien hatten sie je
// eigen definiert, TS2308 beim Vereinen war die ehrliche Folge.
import { DATENREGEL_KRITERIENFELDER, datenregelKriterienSchema, datenregelStatusSchema, datenregelKonto, } from './datenregeln.js';
import { datenregelHerkunftVorlageSchema, datenregelVorlageSchema } from './datenregel-satz.js';
import { isoDate, uuid } from './posting.js';
/**
 * Die Verträge der **Schreibstrecke** der Datenregeln — Auftrag `R-drcaps`
 * (2026-09-01), Task 5 des Plans
 * `docs/superpowers/plans/2026-09-01-datenregeln-umsetzung.md`.
 *
 * **Warum diese Datei neben `datenregeln.ts` steht und nicht darin.** Die
 * Regelsprache selbst — welcher Operator auf welchem der sechs Felder erlaubt
 * ist (`D-3`, `D-4`, `D-5`) — entsteht in `R-drsprache` und liegt dort in
 * `datenregeln.ts`. Was hier steht, ist der UMSCHLAG: was ein Aufrufer
 * schickt, um eine Regel anzulegen, fortzuschreiben, stillzulegen oder
 * scharfzuschalten, und was er zurückbekommt. Zwei Dateien sind eine kleine,
 * benannte Naht zwischen zwei gleichzeitig laufenden Aufträgen; ein
 * gemeinsamer Text wäre ein Konflikt in jeder Zeile.
 *
 * **Die benannte Grenze dieser Datei ist seit `R-drmaske` (2026-09-01)
 * geschlossen — an genau der einen Zeile, die ihr Vorgänger angekündigt
 * hat.** Bis dahin stand hier ein `datenregelKriterienUmschlagSchema`, das
 * die HÜLLE der Kriterien prüfte und ausdrücklich nicht ihren Inhalt: Namen
 * aus dem Katalog, mindestens einer gesetzt, nirgends eine JSON-Zahl. Das
 * war die ehrliche Form, solange `R-drsprache` parallel lief und sein
 * Schema noch nicht existierte. Es existiert seit Zug 107; die
 * Fassungsmerkmale unten nehmen deshalb {@link datenregelKriterienSchema}
 * entgegen, und der Umschlag ist ersatzlos entfallen.
 *
 * **Die drei Zusagen des Umschlags gehen dabei nicht verloren, sie werden
 * schärfer.** Ein unbekannter Feldname fällt an `.strict()` des Katalogs
 * (`DATENREGEL_KRITERIUM_UNBEKANNT`, `D-9`); ein leeres Objekt an dessen
 * `superRefine` (`DATENREGEL_KEIN_KRITERIUM`); und eine JSON-**Zahl** fällt
 * jetzt an jedem einzelnen Feld statt an einer rekursiven Suche — jeder
 * Kriterienwert ist entweder `kriterientext`, eine Aufzählung oder
 * `nonNegativeAmount`, und alle drei sind Zeichenketten-Schemas, die eine
 * Zahl gar nicht erst annehmen (Hausregel 1). Was die schwächere Prüfung
 * NICHT konnte: die FORM hinter dem Feldnamen halten. `{"betrag": {"von":
 * "12.50"}}` — ohne `operator` — kam durch den Umschlag und landete im
 * `jsonb`; die Ablage ist anfügend (`D-10`), und nichts korrigiert sie je
 * wieder.
 */
// Der Feldkatalog (`DATENREGEL_KRITERIENFELDER`) und die vier Stufen des
// Lebenszyklus (`datenregelStatusSchema`) stehen in `datenregeln.ts` und
// werden von dort importiert. Bis `R-drmaske` (2026-09-01) standen an dieser
// Stelle zwei JSDoc-Blöcke ohne Deklaration darunter -- Rückstand der
// Stapelnaht `sammel107`, die die Doppeldefinitionen entfernt und ihre
// Kommentare stehen gelassen hat. Ein JSDoc ohne Gegenstand beschreibt beim
// nächsten Lesen das, was zufällig darunter steht.
/**
 * Die Stufen, die `datenregel:manage` selbst setzen darf.
 *
 * `vollautomatik` fehlt hier, und das ist der ganze Punkt von `D-2`: Sie
 * entsteht ausschliesslich über `POST …/scharfschalten` und damit
 * ausschliesslich unter `datenregel:scharfschalten`. `stillgelegt` fehlt aus
 * einem anderen Grund — sie hat einen eigenen Endpunkt, weil eine Stilllegung
 * die Kriterien nicht mit ändert.
 */
export const datenregelSetzbarerStatusSchema = z.enum(['entwurf', 'vorschlag']);
/**
 * **Das Kontoformat der Datenregeln** — vier bis sechs Ziffern, getrimmt.
 *
 * Eine Stelle für die Fassung (`konto`) und für die Trefferprobe der Vorschau
 * (`konto` als Vergleichswert, `D-29`): Zwei Abschriften derselben
 * Regex wären die Stelle, an der die Probe ein Konto annimmt, das die Fassung
 * zurückweist. Geprüft wird nur die FORM; ob das Konto zum Buchungsdatum
 * gilt, ist Sache der Auswertung (`E-597`).
 */
// Nicht exportiert: Der OpenAPI-Erzeuger benennt exportierte Vertraege nach ihrem Namen, und
// datenregelKonto (datenregeln.ts) ergibt dieselbe Komponente DatenregelKonto (Naht Stapel 266).
const datenregelKontoSchema = z
    .string()
    .transform((wert) => wert.trim())
    .pipe(datenregelKonto);
/**
 * Die Merkmale, die JEDE Fassung trägt — geteilt zwischen Anlage und
 * Fortschreibung, damit es sie nicht zweimal gibt.
 *
 * `konto` ist die Kontonummer als Text und wird hier ausdrücklich **nicht**
 * gegen den Kontenrahmen gehalten: Der Kontenrahmen ist versioniert, und ob
 * ein Konto AM BUCHUNGSDATUM gilt, kann eine Schreibprüfung nicht wissen. Die
 * Auswertung prüft es über eine nicht werfende `EXISTS`-Abfrage (`E-597`,
 * `R-drsprache`); hier steht nur die Form.
 */
const fassungsmerkmale = {
    kriterien: datenregelKriterienSchema,
    konto: datenregelKontoSchema,
    steuerschluessel: z
        .string()
        .trim()
        .min(1, 'Der Steuerschlüssel ist leer')
        .max(20)
        .nullable()
        .optional()
        .transform((wert) => wert ?? null),
    kundeId: uuid
        .nullable()
        .optional()
        .transform((wert) => wert ?? null),
    kreditorId: uuid
        .nullable()
        .optional()
        .transform((wert) => wert ?? null),
    gueltigAb: isoDate,
};
/**
 * Entweder Kunde oder Kreditor, nie beides (Spec 3.2).
 *
 * Steht hier UND als CHECK `regel_version_eine_gegenpartei` in `0112`,
 * dieselbe Doppelung wie beim Feldkatalog: Der Vertrag gibt die Auskunft, die
 * Bedingung hält den Bestand.
 */
function eineGegenpartei(eingabe, ctx) {
    if (eingabe.kundeId !== null && eingabe.kreditorId !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Regel ordnet entweder einem Kunden oder einem Kreditor zu, nie beiden',
            path: ['kreditorId'],
        });
    }
}
/**
 * `POST /api/v1/datenregeln` — eine Regel anlegen.
 *
 * **Die erste Fassung ist immer ein Entwurf**, und der Status steht deshalb
 * nicht im Rumpf. Der Lebenszyklus (Spec 5) beginnt bei `entwurf`; ihn zu
 * überspringen wäre der einzige Weg, eine Regel wirksam werden zu lassen,
 * ohne dass die Live-Vorschau je gegen den Bestand gelaufen ist. Der Schritt
 * nach `vorschlag` ist eine Fortschreibung, der nach `vollautomatik` eine
 * Scharfschaltung.
 *
 * **`vorlage` — die Herkunft aus dem Vorlagenkatalog** (`DR-vorlagenapi`,
 * Task 16, Spec 17.3, `D-26`, `E-1954`). Wer eine Regel aus einer Vorlage der
 * Galerie übernimmt, schickt Kennung, Fassung und SHA-256 mit, die er gesehen
 * hat; der Dienst nimmt sie nur an, wenn es die Vorlage gibt, die Fassung die
 * AKTUELLE ist und das Fassungsbuch den Hash bestätigt, und schreibt sie dann
 * mit der ersten Fassung (drei Spalten, danach unveränderlich, Migration
 * `0320`). Die übernommene Regel ist eine gewöhnliche Fassung: Kriterien,
 * Konto und Schlüssel darf die Maske vor dem Speichern ändern, und es gibt
 * KEINE Bindung an spätere Vorlagenfassungen. Die Fortschreibung hat dieses
 * Feld nicht — sie stammt vom Menschen und nennt keine Vorlage.
 *
 * **Kein Ersatzkonto** (`D-27`, `E-1957`): `konto` bleibt Pflicht. Hat die
 * Installation das Sollkonto der Vorlage nicht, nennt die Vorlagenliste es als
 * nicht verfügbar, und die Übernahme ohne Konto wird von diesem Vertrag
 * abgewiesen; eingesetzt wird nie eine geratene Nummer.
 */
export const datenregelAnlageSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1, 'Der Regelname fehlt')
        .max(120, 'Der Regelname ist zu lang (höchstens 120 Zeichen)'),
    ...fassungsmerkmale,
    vorlage: datenregelHerkunftVorlageSchema.optional(),
})
    .strict()
    .superRefine(eineGegenpartei);
/**
 * `POST /api/v1/datenregeln/:id/fassungen` — fortschreiben.
 *
 * **Der Name fehlt im Rumpf, und das ist keine Auslassung.** Er steht auf dem
 * Kopf (`regel`), nicht auf der Fassung, und `fameacc_app` hat auf `regel`
 * bewusst kein `UPDATE` (Rechteblock in `0112`): Der Name steht im
 * Begründungstext jeder aus dieser Regel erzeugten Buchung (`D-1`), und ein
 * nachträglich geänderter Name machte jede bereits geschriebene Begründung
 * falsch.
 */
export const datenregelFortschreibungSchema = z
    .object({
    status: datenregelSetzbarerStatusSchema,
    ...fassungsmerkmale,
})
    .strict()
    .superRefine(eineGegenpartei);
/**
 * `POST /api/v1/datenregeln/:id/stilllegen` — stilllegen (`D-15`).
 *
 * **Kein Löschendpunkt, und stattdessen dieser.** Eine gebuchte Kontierung
 * zeigt für immer auf ihre `regel_version`; eine löschbare Zeile bräche den
 * Nachweis. Stillgelegt wird deshalb durch ANFÜGEN einer Fassung mit
 * `status = 'stillgelegt'` — die Kriterien und das Konto der Vorgängerin
 * werden übernommen, weil eine Stilllegung nichts an der Regel ändert,
 * sondern ihre Wirkung beendet.
 */
export const datenregelStilllegungSchema = z.object({ gueltigAb: isoDate }).strict();
/**
 * `POST /api/v1/datenregeln/:id/scharfschalten` — auf Vollautomatik schalten
 * (`D-2`, Owner-Entscheid vom 2026-08-31).
 *
 * Derselbe Rumpf wie die Stilllegung und aus demselben Grund: Eine
 * Scharfschaltung ändert die Regel nicht, sie ändert ihre Stufe. Wer die
 * Kriterien zugleich ändern will, schreibt zuerst fort und schaltet dann
 * scharf — zwei Vorgänge, zwei Fassungen, zwei Verantwortliche auf der Zeile.
 */
export const datenregelScharfschaltungSchema = z.object({ gueltigAb: isoDate }).strict();
/**
 * Eine Fassung, wie die Schreibstrecke sie zurückgibt.
 *
 * Vollständig und nicht gekürzt: Die Antwort ist der Beleg dafür, was
 * angefügt wurde — einschliesslich der beiden Scharfschaltungsstempel, die
 * mit dem `INSERT` entstehen und nie nachgetragen werden (`D-10`).
 */
export const datenregelFassungAntwortSchema = z
    .object({
    id: uuid,
    regelId: uuid,
    name: z.string(),
    nameKey: z.string(),
    status: datenregelStatusSchema,
    kriterien: z.record(z.string(), z.unknown()),
    kriterienAnzahl: z.number().int().min(1).max(DATENREGEL_KRITERIENFELDER.length),
    konto: z.string(),
    steuerschluessel: z.string().nullable(),
    kundeId: uuid.nullable(),
    kreditorId: uuid.nullable(),
    gueltigAb: isoDate,
    gueltigBis: isoDate.nullable(),
    createdAt: z.string(),
    createdBy: uuid,
    scharfgeschaltetVon: uuid.nullable(),
    scharfgeschaltetAm: z.string().nullable(),
})
    .strict();
// ===========================================================================
// DIE LIVE-VORSCHAU -- Auftrag `R-drmaske` (2026-09-01), Task 8 des Plans
// ===========================================================================
/**
 * **Wie viele Treffer die Vorschau höchstens aufzählt** (`E-596`).
 *
 * Die Vorschau beantwortet «was treffe ich?» und nicht «gib mir alles» — sie
 * ist eine Auskunft und kein Export (`D-21`, Spec 9). Die ZAHL der Treffer
 * kommt vollständig ({@link DatenregelVorschauAntwort.anzahl}); die LISTE
 * daneben ist ein Ausschnitt, damit der Anlegende sieht, WAS er trifft, und
 * nicht bloss wie viel.
 *
 * **Die Obergrenze steht im Vertrag und nicht nur in der Abfrage.** Eine
 * Grenze, die allein als `LIMIT` im SQL stünde, liesse sich vom Aufrufer mit
 * `grenze: 100000` aushebeln — und aus der Auskunft würde genau der Export,
 * den `D-21` nicht meint. Ein Wert darüber ist deshalb ein Vertragsfehler
 * (400) und kein still gekürztes Ergebnis: Wer 500 anfragt und 50 bekäme,
 * ohne es zu erfahren, hielte die verkürzte Liste für vollständig.
 */
export const DATENREGEL_VORSCHAU_GRENZE_MAX = 50;
/** Wie viele Treffer die Vorschau aufzählt, wenn der Aufrufer nichts sagt. */
export const DATENREGEL_VORSCHAU_GRENZE_VORGABE = 20;
/**
 * `POST /api/v1/datenregeln/vorschau` — die Live-Vorschau, **nur lesend**.
 *
 * **Warum `POST` für eine Leseauskunft, und warum das kein Widerspruch ist.**
 * Der Rumpf ist ein verschachteltes Kriterienobjekt aus bis zu sechs Feldern
 * mit je einem Operator; in eine Abfragezeile gepresst wäre er entweder ein
 * eigenes Kodierformat (das jemand schreiben und jemand lesen müsste) oder
 * ein JSON-Klumpen in einem Parameter — und Kriterienwerte tragen
 * Verwendungszwecke und Namen, also genau das, was nicht in eine URL gehört,
 * weil URLs im Zugriffsprotokoll stehen. `POST` mit geprüftem Rumpf ist die
 * Form; dass er nichts schreibt, sagt die Strecke selbst (`E-596`) und
 * zusätzlich die Datenbank, die den Vorgang in einer `READ ONLY`-Transaktion
 * fährt.
 *
 * **Es gibt kein `gueltigAb` in diesem Rumpf, und das Konto ist ein
 * VERGLEICHSWERT, keine Prüfung.** Die Vorschau fragt, welche Bewegungen die
 * KRITERIEN treffen — nicht, welche Fassung an einem Tag gilt (das ist
 * {@link wirksameFassungen} auf dem Buchungspfad) und nicht, ob das Zielkonto
 * existiert. Das optionale `konto` (Trefferprobe, `D-29`, Spec 17.4) macht
 * aus der Zahl eine Aufteilung nach Kontierungsstand: Wie viele der Treffer
 * sind noch nicht kontiert, wie viele stehen bereits auf diesem Konto, wie
 * viele auf einem anderen? Ob das Konto im Kontenrahmen steht, wird dabei
 * weiterhin NICHT geprüft — die Maske stellt diese zweite Frage gegen den
 * Kontenrahmen, den sie ohnehin geladen hat, und warnt dort. Warnung und
 * keine Sperre, dieselbe Haltung wie `D-21` sie für die Kollision vorgibt.
 * Ohne `konto` bleibt die Antwort, wie sie war.
 */
export const datenregelVorschauSchema = z
    .object({
    kriterien: datenregelKriterienSchema,
    konto: datenregelKontoSchema.optional(),
    grenze: z
        .number()
        .int('Die Grenze ist eine ganze Zahl')
        .min(1, 'Die Grenze ist mindestens 1')
        .max(DATENREGEL_VORSCHAU_GRENZE_MAX, `Die Vorschau zählt höchstens ${DATENREGEL_VORSCHAU_GRENZE_MAX} Treffer auf; sie ist eine Auskunft und kein Export`)
        .optional(),
})
    .strict();
/** Der Kontierungsstand eines Treffers gegen das Konto der Trefferprobe. */
export const datenregelTrefferstandSchema = z.enum(['offen', 'gleich', 'anders']);
/**
 * Die Aufteilung der Trefferzahl nach Kontierungsstand — Trefferprobe
 * (`D-29`, `E-1958`). Die Summe der drei Zahlen ist `anzahl` der Antwort.
 */
export const datenregelAufteilungSchema = z
    .object({
    offen: z.number().int().min(0),
    gleichKontiert: z.number().int().min(0),
    andersKontiert: z.number().int().min(0),
})
    .strict();
/**
 * Ein einzelner Treffer der Vorschau — Datum, Verwendungszweck, Betrag
 * (Spec 9), dazu der Name der Gegenpartei.
 *
 * **Der Betrag ist eine Zeichenkette und behält sein Vorzeichen.** Er kommt
 * aus `bank_transaction.amount` (`numeric`, vorzeichenbehaftet seit
 * Migration `0030`) und geht als `::text` heraus, nie durch `number`
 * (Hausregel 1). Das Vorzeichen bleibt stehen, weil der Anlegende an dieser
 * Liste erkennen soll, ob er Ein- oder Ausgänge trifft — das Kriterium
 * `betrag` vergleicht zwar ohne Vorzeichen, die ANZEIGE ist aber die
 * Bewegung und nicht das Kriterium.
 */
export const datenregelVorschauTrefferSchema = z
    .object({
    bankTransactionId: uuid,
    buchungsdatum: isoDate,
    verwendungszweck: z.string(),
    betrag: z.string(),
    gegenpartei: z.string().nullable(),
    /**
     * Der Kontierungsstand dieser Bewegung gegen das `konto` der Anfrage —
     * nur MIT `konto` gesetzt (Trefferprobe, `D-29`).
     *
     * - `offen` — es gibt keine Zeile in `kontierung` (dieselbe Aussage wie
     *   die dritte Tür des Automatikpfads).
     * - `gleich` — die wirksame Kontierung trägt mindestens eine Position,
     *   und jede steht auf dem Konto.
     * - `anders` — es gibt eine Zeile, aber nicht auf dem Konto: mindestens
     *   eine Position steht woanders, oder die wirksame Zeile trägt gar keine
     *   Position (`aufgehoben`, `storniert`).
     */
    stand: datenregelTrefferstandSchema.optional(),
    /**
     * Bei `stand = anders`: das Konto der ersten abweichenden Position; `null`,
     * wenn die wirksame Zeile keine Position trägt. Sonst nicht gesetzt.
     */
    anderesKonto: z.string().nullable().optional(),
})
    .strict();
// ===========================================================================
// DIE KOLLISIONSWARNUNG DER VORSCHAU -- Auftrag `R-drkollision` (2026-09-01),
// Task 12 des Plans, Spec Abschnitt 6 (`D-13`, `D-21`)
// ===========================================================================
/**
 * **Warum eine Kollision auf die eine oder die andere Seite fällt.**
 *
 * Die vier Werte sind das Urteil der Kollisionsordnung aus Spec 6, in der
 * Sprache, in der die Maske es hinschreibt — und sie werden nicht
 * nachgerechnet, sondern aus der Reihenfolge abgelesen, die
 * `wirksameFassungen()` liefert (`E-592`).
 *
 * - `code_vor_daten` — eine der vier Code-Regeln hat die Bewegung bereits
 *   gebucht. Code gewinnt immer, ohne Ausnahme (`D-13`), und hier ist es
 *   nicht einmal eine Vorhersage: Die Zeile steht in `kontierung`, und die
 *   dritte Tür verschliesst die Bewegung für jede Datenregel.
 * - `spezifischer` — die bestehende Fassung trägt MEHR Kriterien als die
 *   entworfene und gewinnt deshalb.
 * - `aelter` — beide tragen gleich viele Kriterien; dann entscheidet
 *   `regel.created_at` aufsteigend, und eine Regel, die es noch nicht gibt,
 *   ist jünger als jede, die es gibt.
 * - `unterlegen` — die bestehende Fassung trägt WENIGER Kriterien; die
 *   entworfene Regel gewänne. Sie steht trotzdem in der Liste, weil «ich
 *   nehme dieser Regel Bewegungen weg» dieselbe Auskunft ist wie
 *   umgekehrt — nur mit dem anderen Vorzeichen.
 */
export const datenregelKollisionsgrundSchema = z.enum([
    'code_vor_daten',
    'spezifischer',
    'aelter',
    'unterlegen',
]);
/**
 * **Eine Regel, die dieselben Bewegungen beansprucht** — eine Zeile der
 * Warnung aus `D-21`.
 *
 * > „n Bewegungen träfen auch Regel X (spezifischer)."
 *
 * **Die zwei Seiten werden verschieden gemessen, und das ist keine
 * Nachlässigkeit.** Für eine DATENREGEL ist die Frage beantwortbar, ohne zu
 * raten: Beide Kriteriensätze gehen durch dieselbe Übersetzung
 * (`kriterienNachSql()`), und `anzahl` ist die Zahl der Bewegungen, die beide
 * treffen. Für eine CODE-REGEL ist sie es nicht — ob eine Code-Regel eine
 * noch unkontierte Bewegung beansprucht, entscheidet ihr `greift()` in
 * TypeScript und nicht ihre grobe Vorauswahl in SQL; eine aus der Vorauswahl
 * abgelesene Zahl wäre systematisch zu hoch und damit ein Fehlalarm. Gezählt
 * werden deshalb die Bewegungen, die eine Code-Regel bereits GEBUCHT hat
 * (`kontierung.herkunft` aus `beanspruchteHerkuenfte()`). Das ist eine
 * Messung statt einer Vorhersage — und für den Anlegenden die stärkere
 * Auskunft: Diese Bewegungen sind durch die dritte Tür für jede Datenregel
 * verschlossen, heute und für immer.
 */
export const datenregelKollisionsartSchema = z.enum(['datenregel', 'coderegel']);
export const datenregelKollisionSchema = z
    .object({
    /** `datenregel` oder `coderegel` — siehe den Kopf dieses Schemas. */
    art: datenregelKollisionsartSchema,
    /** Der Regelkopf — nur bei einer Datenregel; eine Code-Regel hat keinen. */
    regelId: uuid.nullable(),
    /** Die Fassung — nur bei einer Datenregel. */
    regelVersionId: uuid.nullable(),
    /**
     * Die Kennung der Code-Regel (`aufladung`, `zd_auszahlung`, …) — nur bei
     * einer Code-Regel. Sie steht neben dem Namen, weil die Nachschau
     * (`GET /api/v1/kontierungen/automatisch?regel=…`) über sie geht.
     */
    kennung: z.string().nullable(),
    /** Der Name, unter dem der Sachbearbeiter die Regel kennt. */
    name: z.string(),
    /**
     * Die Stufe der bestehenden Datenregel-Fassung — `null` bei einer
     * Code-Regel, die keine Stufen kennt.
     *
     * Sie steht mit, weil der Hinweis sonst nicht unterscheidbar wäre: Eine
     * Fassung im Status `vorschlag` konkurriert um den VORSCHLAG (`D-12`),
     * eine im Status `vollautomatik` um die BUCHUNG. Beide sind wirksam
     * (`wirksameFassungen()` führt genau diese zwei), aber sie kosten
     * Verschiedenes.
     */
    status: datenregelStatusSchema.nullable(),
    /** Wie viele der Treffer betroffen sind. Nur echte Kollisionen erscheinen. */
    anzahl: z.number().int().min(1),
    /** Ob JENE Regel nach der Kollisionsordnung gewinnt. */
    gewinnt: z.boolean(),
    grund: datenregelKollisionsgrundSchema,
})
    .strict();
/**
 * Die Antwort der Live-Vorschau.
 *
 * **`anzahl` und `treffer.length` sind zwei verschiedene Zahlen**, und das
 * ist der Kern von `E-596`: `anzahl` ist die VOLLSTÄNDIGE Trefferzahl im
 * Bestand — der Satz «Diese Regel hätte im Bestand N Bewegungen getroffen»
 * aus Spec 9 —, `treffer` sind die jüngsten davon, höchstens `grenze` viele.
 * `gekuerzt` sagt es ausdrücklich, damit die Maske «… und weitere» schreiben
 * kann, statt den Leser die zwei Zahlen selbst vergleichen zu lassen.
 *
 * **`kollisionen` ist seit `R-drkollision` (2026-09-01, Task 12) das
 * angekündigte eigene Feld.** Es sagt, wie viele der Treffer bereits von
 * einer anderen wirksamen Datenregel oder von einer der vier Code-Regeln
 * beansprucht würden — **Hinweis und keine Sperre** (`D-21`): Die Ordnung
 * entscheidet zur Buchungszeit, und eine sperrende Vorschau verböte den
 * legitimen Normalfall, eine enge Regel neben einer breiten. Die Liste ist
 * leer, wenn nichts kollidiert; sie führt nur echte Kollisionen
 * (`anzahl >= 1`) und ist absteigend nach `anzahl` geordnet, damit die
 * teuerste Überschneidung oben steht.
 *
 * Die Antwort war und ist `.strict()` — dieses Feld ist deshalb eine
 * sichtbare Erweiterung und kein stiller Zusatz.
 */
export const datenregelVorschauAntwortSchema = z
    .object({
    anzahl: z.number().int().min(0),
    grenze: z.number().int().min(1).max(DATENREGEL_VORSCHAU_GRENZE_MAX),
    gekuerzt: z.boolean(),
    treffer: z.array(datenregelVorschauTrefferSchema),
    kollisionen: z.array(datenregelKollisionSchema),
    /**
     * **Nur mit `konto` in der Anfrage** (Trefferprobe, `D-29`, Spec 17.4):
     * `offen + gleichKontiert + andersKontiert = anzahl` (`E-1958`). Ohne
     * `konto` fehlt das Feld ganz — die Antwort bleibt die alte, und kein
     * Leser muss eine Null von einem Nichtwissen unterscheiden.
     */
    aufteilung: datenregelAufteilungSchema.optional(),
})
    .strict();
// ===========================================================================
// DIE VORLAGENLISTE -- Auftrag `DR-vorlagenapi` (2026-10-15), Task 16 des Plans
// ===========================================================================
/**
 * Eine Zeile der Vorlagenliste: die Katalogvorlage in ihrer aktuellen Fassung
 * ({@link datenregelVorlageSchema}) und was nur diese Installation weiss
 * (Spec 17.3, `D-27`).
 *
 * - `sha256` ist der Hash genau dieser Fassung ({@link vorlagenHash}); die Maske
 *   schickt ihn mit Kennung und Fassung als `vorlage` der Anlage zurück.
 * - `kontoVerfuegbar` — das Sollkonto der Vorlage hat **heute** eine gültige
 *   Fassung in `account` (`valid_to` ausschliessend). `false` heisst: Die Vorlage
 *   bleibt sichtbar und gekennzeichnet, die Übernahme lässt das Konto leer.
 * - `kontoTitel` — der Titel des Sollkontos aus `kontenkatalog`; `null`, wenn der
 *   Katalog das Konto nicht führt. Er steht auch bei `kontoVerfuegbar = false`,
 *   damit die Maske das Soll beim Namen nennen kann.
 * - `schluesselVerfuegbar` — der Steuerschlüssel des Ergebnisses gilt **heute**;
 *   `true`, wenn die Vorlage keinen nennt (es fehlt dann nichts).
 */
export const datenregelVorlagenEintragSchema = datenregelVorlageSchema
    .extend({
    sha256: z.string().regex(/^[0-9a-f]{64}$/u, 'Ein SHA-256 sind 64 kleine Hexziffern'),
    kontoVerfuegbar: z.boolean(),
    kontoTitel: z.string().nullable(),
    schluesselVerfuegbar: z.boolean(),
})
    .strict();
/**
 * `GET /api/v1/datenregeln/vorlagen` -- der Katalog mit der Verfügbarkeit von
 * Konto und Schlüssel, in der Reihenfolge der Katalogdatei.
 */
export const datenregelVorlagenListeSchema = z
    .object({ vorlagen: z.array(datenregelVorlagenEintragSchema) })
    .strict();
//# sourceMappingURL=datenregeln-api.js.map